import React, { useState, useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useScreenSize } from '@/hooks';
import { CRISTP_ID, addNewStyle, wait } from '@/utils';
import ProfileMenuMobile from '@/components/common/ProfileMenu/ProfileMenuMobile';
import styles from './BottomTab.module.css';

const BOTTOM_ICONS = {
  Home: '/assets/images/new-bottom-icons/home.svg',
  Games: '/assets/images/new-bottom-icons/games.svg',
  Trade: '/assets/images/newicons/p2p.svg',
  Sports: '/assets/images/newicons/sports.svg',
  Menu: '/assets/images/new-bottom-icons/menu.svg',
};

/**
 * BottomTab Component
 * Mobile bottom navigation bar
 * Only visible on mobile devices
 * Matches old SkylaBottomTab structure
 */
const BottomTab = () => {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useScreenSize();
  const isMobileOrTablet = isMobile || isTablet;
  const location = useLocation();
  const [loaded, setLoaded] = useState(false);
  const [navItems, setNavItems] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeTab, setActiveTab] = useState('');
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  useLayoutEffect(() => {
    if (!isMobileOrTablet) return;

    const token = localStorage.getItem('token');
    const loggedIn = !!token;

    const getItems = (loggedIn) => [
      { path: '/', label: t('common.home'), id: 'Home' },
      { path: '/casino', label: t('common.games'), id: 'Games' },
      ...(loggedIn ? [{ path: '/trade', label: t('nav.trade'), id: 'Trade' }] : []),
      { path: '/sports/popular', label: t('common.sports'), id: 'Sports' },
      { path: '/menu', label: t('common.menu'), id: 'Menu', onClick: () => setProfileMenuOpen(true) },
    ];

    const getActiveId = (itemList) => {
      const p = location.pathname;
      const found = itemList.find(
        (item) =>
          item.path === p ||
          (item.path === '/' && (p === '/' || p === '/login' || p === '/wallet')) ||
          (item.path === '/casino' && p.startsWith('/casino')) ||
          (item.path === '/trade' && p.startsWith('/trade')) ||
          (item.path === '/sports/popular' && p.startsWith('/sports'))
      );
      return found?.id || '';
    };

    const checkAuthStatus = () => {
      const tk = localStorage.getItem('token');
      const loggedIn = !!tk;
      setIsLoggedIn(loggedIn);
      const newItems = getItems(loggedIn);
      setNavItems(newItems);
      setActiveTab(getActiveId(newItems));
    };

    const items = getItems(loggedIn);
    setIsLoggedIn(loggedIn);
    setNavItems(items);
    setActiveTab(getActiveId(items));

    window.addEventListener('storage', checkAuthStatus);
    return () => window.removeEventListener('storage', checkAuthStatus);
  }, [location.pathname, isMobileOrTablet]);

  const support = () => {
    window.$crisp = [];
    window.CRISP_WEBSITE_ID = CRISTP_ID;

    window.$crisp.push(["config", "container:fullpage", true]);
    window.$crisp.push(["config", "position:reverse", true]);
    window.$crisp.push(["config", "hide:on:mobile", false]);
    window.$crisp.push(["config", "button:hide", true]);

    if (!loaded) {
      setLoaded(true);
      const script = document.createElement('script');
      script.src = "https://client.crisp.chat/l.js";
      script.id = "chtt";
      script.async = true;

      script.onload = function () {
        setTimeout(() => {
          window.$crisp.push(["do", "chat:open"]);
          const crispStyles = `
            .crisp-client .cc-1xry { display: none !important; }
            .crisp-client .cc-kv6t { visibility: visible !important; opacity: 1 !important; display: block !important; }
            .crisp-client .cc-k5k0 { visibility: visible !important; opacity: 1 !important; display: block !important; }
            @media screen and (max-width: 768px) {
              #crisp-chatbox { display: block !important; z-index: 1000000 !important; }
              .crisp-client .cc-kv6t { max-height: 100% !important; height: 100% !important; }
            }
          `;
          const styleSheet = document.createElement("style");
          styleSheet.id = "crisp-custom-styles";
          styleSheet.innerText = crispStyles;
          document.head.appendChild(styleSheet);
        }, 1000);
      };

      document.head.appendChild(script);
    } else {
      window.$crisp.push(["do", "chat:open"]);
    }

    addNewStyle("#crisp-chatbox { display: block !important; }");
    addNewStyle(".crisp-client .cc-1xry { display: none !important; }");

    wait(2000).then(() => {
      const close = document.querySelector(".cc-wdhl");
      if (close) {
        close.addEventListener("click", function () {
          addNewStyle("#crisp-chatbox { display: none !important; }");
          addNewStyle(".crisp-client { display: none !important; visibility: hidden !important; }");
          window.$crisp.push(["do", "chat:hide"]);
          window.$crisp.push(["do", "session:reset"]);
          const crispScript = document.getElementById("chtt");
          if (crispScript) crispScript.remove();
          const customStyles = document.getElementById("crisp-custom-styles");
          if (customStyles) customStyles.remove();
          setLoaded(false);
        });
      }
    });
  };

  const manageClick = (id, itemOnClick) => {
    setActiveTab(id);
    if (itemOnClick) itemOnClick();
  };


  if (!isMobileOrTablet) {
    return null; // Only show on mobile & tablet
  }

  return (
    <nav className={styles.bottomTab}>
      <div className={styles.bottomTabContainer}>
        {navItems.map((item) => {
          const isActive = activeTab === item.id;
          const iconSrc = BOTTOM_ICONS[item.id] || BOTTOM_ICONS.Menu;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`${styles.bottomTabItem} ${isActive ? styles.active : ''}`}
              onClick={() => manageClick(item.id, item.onClick)}
            >
              <div className={`${styles.bottomTabIcon} ${isActive ? styles.active : ''}`}>
                <img
                  src={iconSrc}
                  alt={item.id}
                  className={styles.bottomTabIconImg}
                />
              </div>
              <span className={styles.bottomTabLabel}>
                {item.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomTab;
